/**
 * Plain text for non-browser crawlers (GEO). Walks Editor.js and report-block
 * payloads and keeps prose, skipping URLs, SVG, ids, and live-slot hints.
 */

const MAX_PARAGRAPHS = 500;
const MAX_CHARS = 120_000;
const MAX_DEPTH = 10;

const PREFERRED_KEYS = [
  'text',
  'headline',
  'title',
  'lede',
  'line',
  'body',
  'content',
  'paragraphs',
  'items',
  'contents',
  'summary',
  'description',
  'quote',
  'message',
  'figure',
  'citation',
  'caption',
  'imageCaption',
];

const SKIP_KEYS = new Set([
  'id',
  'url',
  'src',
  'href',
  'file',
  'icon',
  'type',
  'key',
  'slug',
  'class',
  'style',
  'mime',
  'version',
  'time',
  'visualSvg',
  'imageUrl',
  'depth',
  'sourceTag',
  'sectionId',
  'sortOrder',
  'level',
  'binds',
  'liveBind',
  'slotKey',
  'documentId',
  'uuid',
]);

interface Budget {
  chars: number;
}

/** Paragraphs of human-readable copy from an Editor.js payload, JSON string, or report block tree. */
export function readableParagraphsFromEditorPayload(payload: unknown): string[] {
  const out: string[] = [];
  walk(coercePayload(payload), undefined, 0, out, { chars: 0 });
  return out;
}

export function dictionaryReadableParagraphs(term: {
  definition?: string | null;
  definitionDocument?: {
    publishedVersion?: { content?: string | null } | null;
    workingVersion?: { content?: string | null } | null;
  } | null;
}): string[] {
  const published = readableParagraphsFromEditorPayload(
    term.definitionDocument?.publishedVersion?.content,
  );
  const body = published.length
    ? published
    : readableParagraphsFromEditorPayload(term.definitionDocument?.workingVersion?.content);
  const short = plainText(term.definition ?? '');
  if (!short) {
    return body;
  }
  if (body.join('\n').toLowerCase().includes(short.toLowerCase())) {
    return body;
  }
  return [short, ...body];
}

function coercePayload(payload: unknown): unknown {
  if (typeof payload !== 'string') {
    return payload;
  }
  const trimmed = payload.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return trimmed;
  }
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return trimmed;
  }
}

function walk(
  value: unknown,
  key: string | undefined,
  depth: number,
  out: string[],
  budget: Budget,
): void {
  if (depth > MAX_DEPTH || out.length >= MAX_PARAGRAPHS || budget.chars >= MAX_CHARS) {
    return;
  }
  if (value == null) {
    return;
  }
  if (typeof value === 'string') {
    if (key && SKIP_KEYS.has(key)) {
      return;
    }
    if (isSkippableString(value)) {
      return;
    }
    const trimmed = value.trim();
    if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length > 1) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (parsed && typeof parsed === 'object') {
          walk(parsed, key, depth + 1, out, budget);
          return;
        }
      } catch {
        // Prose that happens to start with a brace.
      }
    }
    pushParagraph(plainText(trimmed), out, budget);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      walk(item, key, depth + 1, out, budget);
    }
    return;
  }
  if (typeof value !== 'object') {
    return;
  }

  const record = value as Record<string, unknown>;
  const keys = orderedKeys(record);
  for (const childKey of keys) {
    walk(record[childKey], childKey, depth + 1, out, budget);
  }
}

function orderedKeys(record: Record<string, unknown>): string[] {
  const present = new Set(Object.keys(record));
  const preferred = PREFERRED_KEYS.filter((key) => present.has(key) && !SKIP_KEYS.has(key));
  const rest = Object.keys(record).filter(
    (key) => !SKIP_KEYS.has(key) && !PREFERRED_KEYS.includes(key),
  );
  return [...preferred, ...rest];
}

function pushParagraph(text: string, out: string[], budget: Budget): void {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (cleaned.length < 2 || isLiveHint(cleaned)) {
    return;
  }
  if (out.length > 0 && out[out.length - 1] === cleaned) {
    return;
  }
  if (budget.chars + cleaned.length > MAX_CHARS || out.length >= MAX_PARAGRAPHS) {
    return;
  }
  out.push(cleaned);
  budget.chars += cleaned.length;
}

function isLiveHint(text: string): boolean {
  return text.startsWith('Live —') || text.startsWith('Live -');
}

function isSkippableString(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return true;
  }
  if (trimmed.startsWith('<svg') || trimmed.includes('<svg')) {
    return true;
  }
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) {
    return true;
  }
  return false;
}

function plainText(value: string): string {
  return decodeEntities(value.replace(/<[^>]+>/g, ' ')).replace(/\s+/g, ' ').trim();
}

function decodeEntities(value: string): string {
  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}
